import { useEffect, useState } from "react";
import { Copy, X, Loader2, AlertCircle } from "lucide-react";
import { cloneWorkflow } from "../../services/workflowService";

/**
 * Clone a workflow into this organization.
 *
 * Same tenant, so endpoint references stay valid and nothing is redacted — the
 * copy behaves exactly like the original. If the name is already taken the
 * server numbers it rather than failing.
 */
export default function CloneWorkflowDialog({ workflow, onClose, onCloned }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(workflow ? `${workflow.name} (copy)` : "");
    setError(null);
    setBusy(false);
  }, [workflow]);

  if (!workflow) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    const res = await cloneWorkflow(workflow.id, { name: name.trim() });
    setBusy(false);
    if (res.success) {
      onCloned?.(res.data);
      onClose?.();
    } else {
      setError(res.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white">
            <Copy className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900">Clone workflow</h2>
            <p className="text-xs text-gray-500 truncate">
              A new v1 copy of “{workflow.name}” in this organization.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Name
            </span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
            />
          </label>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            Clone
          </button>
        </div>
      </form>
    </div>
  );
}
